import { motion, useReducedMotion } from "framer-motion";
import { getTempo } from "../utils/tempo";

// Cropped transparent electronics cutouts in /public/devices.
const DEVICES = {
  airpods: { src: "/devices/airpods.png", glow: "rgba(244,244,239,0.35)", alt: "AirPods earbuds in charging case" },
  speaker: { src: "/devices/speaker.png", glow: "rgba(61,111,224,0.4)", alt: "Portable wireless speaker" },
  watch: { src: "/devices/watch.png", glow: "rgba(233,180,76,0.35)", alt: "Smartwatch" },
  headphones: { src: "/devices/headphones.png", glow: "rgba(124,131,219,0.35)", alt: "Over-ear headphones" },
  laptop: { src: "/devices/laptop.png", glow: "rgba(127,224,195,0.35)", alt: "MacBook laptop" },
  broken: { src: "/devices/broken-phone.png", glow: "rgba(46,107,230,0.45)", alt: "Phone with a cracked screen" },
  cracked: { src: "/devices/cracked-iphone.png", glow: "rgba(46,107,230,0.5)", alt: "iPhone with a cracked back glass" },
  "watch-ultra": { src: "/devices/watch-ultra.png", glow: "rgba(233,180,76,0.35)", alt: "Smartwatch with black band" },
  "watch-sport": { src: "/devices/watch-sport.png", glow: "rgba(79,179,191,0.35)", alt: "Smartwatch" },
  gaming: { src: "/devices/gaming-headset.png", glow: "rgba(124,131,219,0.4)", alt: "Gaming headset with microphone" },
  keyboard: { src: "/devices/keyboard-rgb.png", glow: "rgba(61,111,224,0.45)", alt: "RGB backlit gaming keyboard" },
  "watch-galaxy": { src: "/devices/watch-galaxy.png", glow: "rgba(61,111,224,0.4)", alt: "Samsung Galaxy smartwatch" },
  cable: { src: "/devices/cable.png", glow: "rgba(244,244,239,0.35)", alt: "Fast charging cables" },
  // planets
  earth: { src: "/planets/earth.png", glow: "rgba(77,164,255,0.55)", alt: "Planet Earth" },
  moon: { src: "/planets/moon.png", glow: "rgba(244,244,239,0.4)", alt: "The Moon" },
  saturn: { src: "/planets/saturn.png", glow: "rgba(232,201,135,0.55)", alt: "Saturn with its rings" },
  jupiter: { src: "/planets/jupiter.png", glow: "rgba(230,150,90,0.55)", alt: "Jupiter" },
  pluto: { src: "/planets/pluto.png", glow: "rgba(220,200,180,0.5)", alt: "Pluto" },
};

export const deviceMeta = (d) => DEVICES[d] || DEVICES.airpods;

// Slowly drifting device cutout for section backgrounds. Transform-only, cheap on GPU.
export const DeviceDrift = ({
  device,
  className = "",
  imgClassName = "",
  dur = 12,
  delay = 0,
  spin = false,
  opacity = "opacity-[0.22]",
}) => {
  const v = deviceMeta(device);
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden="true"
      className={`pointer-events-none absolute select-none ${opacity} ${className}`}
      animate={reduce ? {} : { y: [0, -14, 0], x: [0, 6, 0], rotate: [-1.8, 1.8, -1.8], rotateY: [-5, 5, -5] }}
      style={{ transformPerspective: 900 }}
      transition={{ duration: dur * getTempo(), delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="relative">
        {/* cheap glow disc — a drop-shadow filter on an animating layer
            repaints every frame (lag); a blurred disc composites free */}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: "130%", height: "130%", background: `radial-gradient(circle, ${v.glow} 0%, transparent 70%)` }}
        />
        <motion.img
          src={v.src}
          alt=""
          draggable="false"
          loading="lazy"
          decoding="async"
          className={`relative w-auto ${imgClassName}`}
          {...(spin && !reduce
            ? {
                animate: { rotate: [0, 360] },
                transition: { duration: 60, repeat: Infinity, ease: "linear" },
              }
            : {})}
        />
      </div>
    </motion.div>
  );
};
